import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import type { ModelVersionChecks } from "../../../api/content/ontologyApi";
import type { LoadState } from "../../../shared/loading/useLoad";
import IconButton from "../../../ui/buttons/IconButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import CheckResultList from "../../reviews/checks/CheckResultList";

/**
 * The results behind the version's check chip, in a panel beside the terms: the model's
 * reasoning, shapes and competency questions, then the process graph's, each in the one
 * shared rendering of a check run (`CheckResultList`).
 */
export default function VersionChecksPanel({
  state,
  onRetry,
  onClose,
}: {
  state: LoadState<ModelVersionChecks>;
  onRetry: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <aside
      className="app-card flex max-h-[70vh] w-full flex-col gap-3 overflow-auto p-3 lg:max-h-none lg:w-[28rem] lg:shrink-0"
      aria-label={t("ontologyViewer.checks.title")}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="app-title text-sm font-semibold">{t("ontologyViewer.checks.title")}</h3>
        <IconButton label={t("ontologyViewer.checks.close")} onClick={onClose}>
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </IconButton>
      </div>
      {state.status !== "loaded" ? (
        <LoadStatus state={state} loadingLabel={t("ontologyViewer.checks.running")} onRetry={onRetry} compact />
      ) : (
        <>
          <CheckResultList results={state.value.results} />
          <section className="space-y-2" aria-label={t("ontologyViewer.checks.process")}>
            <h4 className="app-label text-xs">{t("ontologyViewer.checks.process")}</h4>
            <CheckResultList results={state.value.processResults} />
          </section>
        </>
      )}
    </aside>
  );
}
